import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SpeakerHigh, SpeakerX, MusicNotes } from "@phosphor-icons/react";
import { useMusic } from "@/context/MusicContext";

export default function MusicBar() {
  const { playing, muted, toggle, toggleMute, phase } = useMusic();
  const label = phase === "music" ? "Rise Up · the movement" : "Imagine · Ryan's manifesto";
  const [collapsed, setCollapsed] = useState(false);
  const timer = useRef(null);

  const scheduleHide = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCollapsed(true), 4000);
  };
  const expand = () => { clearTimeout(timer.current); setCollapsed(false); };

  useEffect(() => { scheduleHide(); return () => clearTimeout(timer.current); }, []);

  return (
    <div
      className="fixed bottom-4 left-4 sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 z-40"
      onMouseEnter={expand}
      onMouseLeave={scheduleHide}
      data-testid="music-bar"
    >
      <AnimatePresence mode="wait" initial={false}>
        {collapsed ? (
          <motion.button
            key="mini"
            initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 0.85, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
            onClick={expand}
            data-testid="music-bar-mini"
            className="h-10 w-10 grid place-items-center rounded-full bg-popover/85 backdrop-blur border border-primary/40 text-primary neon-glow hover:opacity-100"
            title="Now playing — tap to expand"
          >
            <MusicNotes weight="fill" size={16} className={playing ? "animate-pulse" : ""} />
          </motion.button>
        ) : (
          <motion.div
            key="full"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-popover/90 backdrop-blur border border-primary/40 neon-glow"
          >
            <button onClick={toggle} data-testid="music-toggle"
              className="h-9 w-9 grid place-items-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              {playing ? <Pause weight="fill" size={16} /> : <Play weight="fill" size={16} className="ml-0.5" />}
            </button>
            <div className="hidden sm:flex flex-col leading-none pr-1">
              <span className="overline text-primary flex items-center gap-1"><MusicNotes weight="fill" size={11} /> Now playing</span>
              <span className="text-xs text-white font-modern mt-1 max-w-[160px] truncate">{label}</span>
            </div>
            <button onClick={toggleMute} data-testid="music-mute"
              className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-white transition-colors">
              {muted ? <SpeakerX size={16} /> : <SpeakerHigh size={16} />}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
